import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Headphones, Plus, Trash2, Pencil, RefreshCw, CheckCircle2, ShieldOff, X, Check } from "lucide-react";
import PanelState, { extractApiError } from "@/components/PanelState";
import { useAuth } from "@/lib/auth";

// ============================================================================
// Platform-admin / Owner CSR Staff CRUD (pt.82, 2026-09-16). Mirrors the
// AdminTechProfiles layout but backed by /api/csr-profiles. A CSR profile is
// just {name, active, aliases, email} — no license/pay_type semantics. The
// run_compute engine snapshots the active roster at compute time and evenly
// splits csr_combined_line.total_dollars across active profiles.
// ============================================================================

const LABEL_CLS = "text-[11px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold";

export default function AdminCsrProfiles() {
  const { user } = useAuth();
  const isPlatformAdmin = user?.role === "platform_admin";
  const [tenants, setTenants] = useState([]);
  const [tenantSlug, setTenantSlug] = useState("");
  const [tenantsLoad, setTenantsLoad] = useState({ status: "loading", error: null });

  const loadTenants = useCallback(async () => {
    setTenantsLoad({ status: "loading", error: null });
    if (!isPlatformAdmin) {
      try {
        const { data } = await api.get("/tenants/current");
        if (data?.slug) {
          setTenants([{ slug: data.slug, business_name: data.business_name || data.slug }]);
          setTenantSlug(data.slug);
          setTenantsLoad({ status: "ok", error: null });
        } else {
          throw new Error("Tenant lookup returned no slug");
        }
      } catch (err) {
        setTenants([]);
        setTenantsLoad({ status: "error", error: extractApiError(err) });
      }
      return;
    }
    try {
      const { data } = await api.get("/tenants");
      const rows = (Array.isArray(data) ? data : []).filter((t) => t.slug);
      setTenants(rows);
      setTenantsLoad({ status: "ok", error: null });
      if (!tenantSlug && rows.length) setTenantSlug(rows[0].slug);
    } catch (err) {
      setTenants([]);
      setTenantsLoad({ status: "error", error: extractApiError(err) });
    }
  }, [tenantSlug, isPlatformAdmin]);

  useEffect(() => { loadTenants(); }, [loadTenants]);

  return (
    <div data-testid="admin-csr-profiles-page" className="space-y-5">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[.15em] text-[color:var(--cc-ink-muted)] font-semibold">
            {isPlatformAdmin ? "Platform admin" : "Admin"}
          </div>
          <h1 className="font-display font-bold text-[26px] sm:text-[32px] tracking-tight text-[#0e1420] mt-1 inline-flex items-center gap-2.5">
            <Headphones size={22} strokeWidth={1.6} className="text-[#0072bc]" />
            CSR Staff
          </h1>
          <p className="text-[13.5px] text-[color:var(--cc-ink-muted)] mt-1">
            Non-technician staff who earn CSR / follow-up commission. Roster is snapshotted
            into each payroll compute and the CSR commission pool is split evenly across
            all active profiles.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {isPlatformAdmin ? (
            <>
              <label className={LABEL_CLS}>Tenant</label>
              <select
                data-testid="csr-profiles-tenant-select"
                value={tenantSlug}
                onChange={(e) => setTenantSlug(e.target.value)}
                className="border border-[#e7e9ee] rounded-lg px-3 py-2 text-[13px] bg-white outline-none focus:border-[#0072bc] min-w-[220px]"
              >
                {tenants.map((t) => (
                  <option key={t.slug} value={t.slug}>{t.business_name} · /{t.slug}</option>
                ))}
              </select>
            </>
          ) : (
            <span className="text-[12.5px] text-[color:var(--cc-ink-muted)]" data-testid="csr-profiles-tenant-label">
              Tenant: <b className="text-[#0e1420]">{tenants[0]?.business_name || tenantSlug}</b>
            </span>
          )}
        </div>
      </header>

      {!tenantSlug ? (
        <PanelState
          status={tenantsLoad.status} error={tenantsLoad.error}
          isEmpty={tenants.length === 0} emptyTitle="No tenants available"
          emptyBody="Provision a tenant first." testIdPrefix="csr-profiles-tenants"
        />
      ) : (
        <ProfilesTable tenantSlug={tenantSlug} />
      )}
    </div>
  );
}

function ProfilesTable({ tenantSlug }) {
  const [rows, setRows] = useState([]);
  const [load, setLoad] = useState({ status: "loading", error: null });
  const [showInactive, setShowInactive] = useState(true);
  const [editing, setEditing] = useState(null);
  const [confirmingDelete, setConfirmingDelete] = useState(null);

  const load_ = useCallback(async () => {
    setLoad({ status: "loading", error: null });
    try {
      const { data } = await api.get("/csr-profiles", {
        params: { tenant_slug: tenantSlug, include_inactive: true },
      });
      setRows(Array.isArray(data) ? data : []);
      setLoad({ status: "ok", error: null });
    } catch (err) {
      setRows([]);
      setLoad({ status: "error", error: extractApiError(err) });
    }
  }, [tenantSlug]);

  useEffect(() => { load_(); }, [load_]);

  const visible = rows.filter((r) => showInactive ? true : r.active !== false);
  const activeCount = rows.filter((r) => r.active !== false).length;

  return (
    <>
      {activeCount > 0 && (
        <div
          className="bg-[#e7f4fb] border border-[#c2dff0] rounded-xl px-4 py-3 text-[12.5px] text-[#1c6a99]"
          data-testid="csr-profiles-split-hint"
        >
          <b>Live split preview:</b> {activeCount} active {activeCount === 1 ? "profile" : "profiles"} → CSR commission pool
          divides <b>{(100 / activeCount).toFixed(activeCount === 3 ? 2 : 1)}%</b> per profile
          {activeCount > 1 && <> (any rounding residual is absorbed on the last profile so per-CSR breakdown sums exactly to <code className="font-mono-data">csr_combined_line.total_dollars</code>).</>}
        </div>
      )}

      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-4 text-[12.5px]">
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              data-testid="csr-profiles-toggle-inactive"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              className="accent-[#0072bc]"
            />
            Show inactive
          </label>
          <span className="text-[color:var(--cc-ink-muted)]">
            {visible.length} of {rows.length} · {activeCount} active
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load_}
            data-testid="csr-profiles-refresh"
            className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold px-3 py-2 border border-[#e7e9ee] rounded-lg hover:bg-[#f5f6f8]"
          >
            <RefreshCw size={14} strokeWidth={1.8} /> Refresh
          </button>
          <button
            onClick={() => setEditing({})}
            data-testid="csr-profiles-new-btn"
            className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold px-3 py-2 bg-[#0072bc] text-white rounded-lg hover:bg-[#005a95]"
          >
            <Plus size={14} strokeWidth={2} /> New CSR profile
          </button>
        </div>
      </div>

      {load.status !== "ok" || visible.length === 0 ? (
        <PanelState
          status={load.status} error={load.error}
          isEmpty={visible.length === 0}
          emptyTitle="No CSR staff yet"
          emptyBody='Click "New CSR profile" to add Katt, Juliana, or any follow-up commission earner.'
          testIdPrefix="csr-profiles"
        />
      ) : (
        <div className="border border-[#e7e9ee] rounded-lg overflow-x-auto bg-white">
          <table className="w-full text-[13px] min-w-[720px]">
            <thead className="bg-[#f5f6f8] text-[11px] uppercase tracking-wider text-[color:var(--cc-ink-muted)]">
              <tr>
                <th className="text-left px-3 py-2.5 font-semibold">Name</th>
                <th className="text-left px-3 py-2.5 font-semibold">Email</th>
                <th className="text-left px-3 py-2.5 font-semibold">Aliases</th>
                <th className="text-left px-3 py-2.5 font-semibold">Status</th>
                <th className="text-right px-3 py-2.5 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((r) => (
                <tr key={r.id} data-testid={`csr-profile-row-${r.id}`} className="border-t border-[#e7e9ee]">
                  <td className="px-3 py-2.5 font-semibold text-[#0e1420]">{r.name}</td>
                  <td className="px-3 py-2.5 text-[12.5px] font-mono-data">
                    {r.email || <span className="text-[color:var(--cc-ink-muted)]">—</span>}
                  </td>
                  <td className="px-3 py-2.5 text-[12.5px]">
                    {(r.aliases || []).length === 0 ? (
                      <span className="text-[color:var(--cc-ink-muted)]">—</span>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {r.aliases.map((a) => (
                          <span key={a} className="inline-flex items-center px-1.5 py-0.5 text-[11px] rounded-full bg-[#f5f6f8] text-[#4b5563] border border-[#e7e9ee] font-mono-data">
                            {a}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2.5">
                    {r.active !== false ? (
                      <span className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-[#0e8a68]">
                        <CheckCircle2 size={13} strokeWidth={2} /> Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-[color:var(--cc-ink-muted)]">
                        <ShieldOff size={13} strokeWidth={2} /> Inactive
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button
                        onClick={() => setEditing(r)}
                        data-testid={`csr-profile-edit-${r.id}`}
                        className="p-1.5 hover:bg-[#f5f6f8] rounded-md"
                        title="Edit"
                      >
                        <Pencil size={14} strokeWidth={1.8} />
                      </button>
                      <button
                        onClick={() => setConfirmingDelete(r)}
                        data-testid={`csr-profile-delete-${r.id}`}
                        className="p-1.5 hover:bg-[#fff5f5] hover:text-[#7a1c1c] rounded-md"
                        title="Delete"
                      >
                        <Trash2 size={14} strokeWidth={1.8} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <CsrProfileModal
          tenantSlug={tenantSlug}
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await load_(); }}
        />
      )}
      {confirmingDelete && (
        <CsrProfileDeleteConfirm
          tenantSlug={tenantSlug}
          profile={confirmingDelete}
          onClose={() => setConfirmingDelete(null)}
          onDeleted={async () => { setConfirmingDelete(null); await load_(); }}
        />
      )}
    </>
  );
}

function CsrProfileModal({ tenantSlug, initial, onClose, onSaved }) {
  const isNew = !initial?.id;
  const [name, setName] = useState(initial?.name || "");
  const [email, setEmail] = useState(initial?.email || "");
  const [aliasesRaw, setAliasesRaw] = useState((initial?.aliases || []).join(", "));
  const [active, setActive] = useState(initial?.active !== false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { setErr("Name is required."); return; }
    setSaving(true); setErr(null);
    const body = {
      name: name.trim(),
      email: email.trim() || null,
      aliases: aliasesRaw.split(",").map((s) => s.trim()).filter(Boolean),
      active,
    };
    try {
      if (isNew) {
        await api.post(`/csr-profiles?tenant_slug=${tenantSlug}`, body);
      } else {
        await api.patch(`/csr-profiles/${initial.id}?tenant_slug=${tenantSlug}`, body);
      }
      await onSaved();
    } catch (e) {
      setErr(extractApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#0e1420]/50 flex items-center justify-center p-4" data-testid="csr-profile-modal">
      <form onSubmit={submit} className="bg-white rounded-xl shadow-xl w-full max-w-[520px] overflow-hidden">
        <div className="px-5 py-4 border-b border-[#e7e9ee] flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold">
              CSR Staff
            </div>
            <h2 className="font-display font-semibold text-[18px]">
              {isNew ? "New CSR profile" : `Edit ${initial.name}`}
            </h2>
          </div>
          <button type="button" onClick={onClose} data-testid="csr-profile-modal-close" className="p-1 hover:bg-[#f5f6f8] rounded">
            <X size={16} />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="text-[12px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold block mb-1.5">Name *</label>
            <input
              data-testid="csr-profile-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Katt"
              className="w-full border border-[#e7e9ee] rounded-lg px-3 py-2 text-[13.5px] focus:border-[#0072bc] focus:outline-none"
            />
          </div>
          <div>
            <label className="text-[12px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold block mb-1.5">Email</label>
            <input
              data-testid="csr-profile-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="katt@example.com"
              className="w-full border border-[#e7e9ee] rounded-lg px-3 py-2 text-[13.5px] focus:border-[#0072bc] focus:outline-none font-mono-data"
            />
          </div>
          <div>
            <label className="text-[12px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold block mb-1.5">Aliases (comma-separated)</label>
            <input
              data-testid="csr-profile-aliases-input"
              value={aliasesRaw}
              onChange={(e) => setAliasesRaw(e.target.value)}
              placeholder="katt, katt williams"
              className="w-full border border-[#e7e9ee] rounded-lg px-3 py-2 text-[13.5px] focus:border-[#0072bc] focus:outline-none font-mono-data"
            />
            <div className="text-[11.5px] text-[color:var(--cc-ink-muted)] mt-1">
              Used by the compute engine's token-set matcher — helps if ST reports vary spellings.
            </div>
          </div>
          <label className="flex items-center gap-2 cursor-pointer select-none text-[13.5px]">
            <input
              type="checkbox"
              data-testid="csr-profile-active-toggle"
              checked={active}
              onChange={(e) => setActive(e.target.checked)}
              className="accent-[#0072bc]"
            />
            Active — include in the CSR commission split
          </label>
          {err && (
            <div className="bg-[#fdecec] border border-[#f7c1c1] rounded-lg px-3 py-2 text-[12.5px] text-[#7a1c1c]" data-testid="csr-profile-modal-err">
              {err}
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t border-[#e7e9ee] flex items-center justify-end gap-2 bg-[#fafbfc]">
          <button type="button" onClick={onClose} className="px-3 py-2 text-[12.5px] font-semibold rounded-lg border border-[#e7e9ee] hover:bg-[#f5f6f8]">
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            data-testid="csr-profile-save-btn"
            className="inline-flex items-center gap-1.5 px-3 py-2 text-[12.5px] font-semibold rounded-lg bg-[#0072bc] text-white hover:bg-[#005a95] disabled:opacity-60"
          >
            <Check size={14} strokeWidth={2} /> {saving ? "Saving…" : (isNew ? "Create profile" : "Save changes")}
          </button>
        </div>
      </form>
    </div>
  );
}

function CsrProfileDeleteConfirm({ tenantSlug, profile, onClose, onDeleted }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  // Backend has no DELETE endpoint (soft-delete via active=false is the pattern);
  // this modal soft-deletes by patching active=false.
  const softDelete = async () => {
    setBusy(true); setErr(null);
    try {
      await api.patch(`/csr-profiles/${profile.id}?tenant_slug=${tenantSlug}`, { active: false });
      await onDeleted();
    } catch (e) {
      setErr(extractApiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 bg-[#0e1420]/50 flex items-center justify-center p-4" data-testid="csr-profile-delete-modal">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-[440px] overflow-hidden">
        <div className="px-5 py-4 border-b border-[#e7e9ee]">
          <h2 className="font-display font-semibold text-[17px] text-[#7a1c1c]">
            Deactivate {profile.name}?
          </h2>
        </div>
        <div className="p-5 text-[13.5px] text-[color:var(--cc-ink-muted)] space-y-2">
          <p>
            This will set the profile to <b>inactive</b> — it stops receiving CSR commission on
            future compute runs. Historical runs already computed keep their snapshot and are unaffected.
          </p>
          <p>You can flip <code className="font-mono-data">active</code> back on any time from the edit modal.</p>
          {err && <div className="bg-[#fdecec] border border-[#f7c1c1] rounded-lg px-3 py-2 text-[12.5px] text-[#7a1c1c]">{err}</div>}
        </div>
        <div className="px-5 py-3 border-t border-[#e7e9ee] flex items-center justify-end gap-2 bg-[#fafbfc]">
          <button onClick={onClose} className="px-3 py-2 text-[12.5px] font-semibold rounded-lg border border-[#e7e9ee] hover:bg-[#f5f6f8]">
            Cancel
          </button>
          <button
            onClick={softDelete}
            disabled={busy}
            data-testid="csr-profile-confirm-delete-btn"
            className="inline-flex items-center gap-1.5 px-3 py-2 text-[12.5px] font-semibold rounded-lg bg-[#7a1c1c] text-white hover:bg-[#5f1414] disabled:opacity-60"
          >
            <Trash2 size={14} strokeWidth={2} /> {busy ? "Deactivating…" : "Deactivate"}
          </button>
        </div>
      </div>
    </div>
  );
}
